import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Container,
  Divider,
  List,
  ListItem,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Cookie as CookieIcon,
  Security as SecurityIcon,
  Analytics as AnalyticsIcon,
  Campaign as MarketingIcon,
  Settings as FunctionalIcon,
} from '@mui/icons-material';

const CookiePolicy = () => {
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={2} sx={{ p: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <CookieIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
          <Typography variant="h3" component="h1" gutterBottom>
            Cookie Policy
          </Typography>
          {/* Last updated date removed for compliance */}
          <Button
            variant="outlined"
            size="small"
            sx={{ mt: 1 }}
            onClick={() => window.dispatchEvent(new Event('gt:open-cookie-settings'))}
          >
            Manage cookie preferences
          </Button>
        </Box>

        <Typography variant="body1" paragraph>
              This Cookie Policy explains how GoTruTax ("we", "us", or "our") uses cookies and similar technologies on our website and services. By using our website, you consent to the use of cookies in accordance with this policy. We are committed to protecting your privacy and complying with all applicable data protection laws, including GDPR.
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h5" gutterBottom>
          What Are Cookies?
        </Typography>
        <Typography variant="body1" paragraph>
          Cookies are small text files that are stored on your computer or mobile device when you visit our website.
          They help us provide you with a better browsing experience, analyze site traffic, and personalize content.
          Cookies can be "persistent" (they remain on your device for a set period) or "session" (they are deleted
          when you close your browser).
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
          Types of Cookies We Use
        </Typography>

        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SecurityIcon color="primary" />
              <Typography variant="h6">Necessary Cookies</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body1" paragraph>
              These cookies are essential for the website to function properly. They enable core functionality
              such as security, network management, and accessibility. You cannot disable these cookies without
              significantly affecting the website's functionality.
            </Typography>
            <Typography variant="subtitle2" gutterBottom>
              Examples:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="Authentication cookies - Keep you logged in during your session" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Security cookies - Protect against cross-site request forgery" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Session cookies - Maintain your session state" />
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AnalyticsIcon color="primary" />
              <Typography variant="h6">Analytics Cookies</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body1" paragraph>
              These cookies help us understand how visitors interact with our website by collecting and reporting
              information anonymously. This information helps us improve our website's performance and user experience.
            </Typography>
            <Typography variant="subtitle2" gutterBottom>
              Examples:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="Google Analytics - Tracks page views, session duration, and user behavior" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Performance monitoring - Measures page load times and errors" />
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MarketingIcon color="primary" />
              <Typography variant="h6">Marketing Cookies</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body1" paragraph>
              These cookies are used to track visitors across websites to display ads that are relevant and
              engaging for individual users. They remember your preferences and help us show you advertisements
              that may be of interest to you.
            </Typography>
            <Typography variant="subtitle2" gutterBottom>
              Examples:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="Advertising cookies - Track visits from advertisement campaigns" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Retargeting cookies - Show relevant ads based on your browsing history" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Social media cookies - Enable social media sharing and tracking" />
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FunctionalIcon color="primary" />
              <Typography variant="h6">Functional Cookies</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body1" paragraph>
              These cookies enable the website to provide enhanced functionality and personalization. They may be
              set by us or by third-party providers whose services we have added to our pages.
            </Typography>
            <Typography variant="subtitle2" gutterBottom>
              Examples:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="Language preference cookies - Remember your language settings" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Theme cookies - Remember your display preferences" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Chat widget cookies - Enable live chat functionality" />
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h5" gutterBottom>
          Third-Party Cookies
        </Typography>
        <Typography variant="body1" paragraph>
          Some cookies on our website are set by third-party services that appear on our pages. We have no control
          over these cookies, and they are subject to the respective third party's privacy policy.
        </Typography>
        <Typography variant="subtitle2" gutterBottom>
          Third parties that may set cookies include:
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText primary="Google Analytics - For website analytics and performance monitoring" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Google Ads - For advertising and retargeting" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Stripe - For payment processing" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Shopify - For e-commerce integration" />
          </ListItem>
        </List>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h5" gutterBottom>
          Managing Your Cookie Preferences
        </Typography>
        <Typography variant="body1" paragraph>
          You can control and manage cookies in various ways:
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText primary="Use our cookie consent banner to accept or reject different categories of cookies" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Most web browsers allow you to control cookies through their settings" />
          </ListItem>
          <ListItem>
            <ListItemText primary="You can delete all cookies that are already on your computer" />
          </ListItem>
          <ListItem>
            <ListItemText primary="You can set most browsers to prevent cookies from being placed" />
          </ListItem>
        </List>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Note: If you disable cookies, some features of our website may not work properly. GoTruTax is not responsible for any loss of functionality due to disabled cookies.
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h5" gutterBottom>
          Your Rights Under GDPR
        </Typography>
        <Typography variant="body1" paragraph>
          Under the General Data Protection Regulation (GDPR), you have the following rights regarding cookies and your data:
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText primary="Right to be informed - We must clearly explain how we use cookies" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Right to access - You can request information about the data we hold about you" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Right to rectification - You can have inaccurate data corrected" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Right to erasure - You can request deletion of your personal data" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Right to restrict processing - You can limit how we use your data" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Right to data portability - You can receive your data in a structured format" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Right to object - You can object to certain types of data processing" />
          </ListItem>
        </List>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h5" gutterBottom>
          Contact Us
        </Typography>
        <Typography variant="body1" paragraph>
          If you have any questions about our use of cookies or this Cookie Policy, please contact us:
        </Typography>
            <Typography variant="body1">
              Email: support@gotrutax.com<br />
              Address: [Your Business Address]<br />
              Phone: [Your Phone Number]
            </Typography>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h5" gutterBottom>
          Changes to This Policy
        </Typography>
    <Typography variant="body1" paragraph>
      We may update this Cookie Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We will notify you of any material changes by posting the updated policy on our website. Continued use of the website constitutes acceptance of any changes.
        </Typography>
      </Paper>
    </Container>
  );
};

export default CookiePolicy;
